import { useEffect, useState } from 'react';
import { listSpheres } from './api.js';
import type { Sphere } from './types.js';

/**
 * Spheres change rarely (renamed in Settings at most), so the list is fetched
 * once per page load and shared. A failed fetch is not cached.
 */
let cache: Promise<Sphere[]> | null = null;

function loadSpheres(): Promise<Sphere[]> {
  cache ??= listSpheres().catch((err: unknown) => {
    cache = null;
    throw err;
  });
  return cache;
}

/** Tests only: forget the cached list. */
export function resetSphereCache(): void {
  cache = null;
}

/** Active spheres in their configured order; [] until loaded (or on error). */
export function useSpheres(): Sphere[] {
  const [spheres, setSpheres] = useState<Sphere[]>([]);
  useEffect(() => {
    let alive = true;
    loadSpheres().then(
      (all) => {
        if (alive) setSpheres(all.filter((s) => !s.archived).sort((a, b) => a.sort - b.sort));
      },
      () => {
        /* chips and colours degrade to neutral */
      },
    );
    return () => {
      alive = false;
    };
  }, []);
  return spheres;
}

/** NULL sphere means personal (spec §3.0). */
export function sphereOf<T extends { key: string }>(spheres: readonly T[], key: string | null | undefined): T | undefined {
  return spheres.find((s) => s.key === (key ?? 'personal'));
}

/** The sphere's colour, or the muted text token when unknown. */
export function sphereColor(spheres: readonly Sphere[], key: string | null | undefined): string {
  return sphereOf(spheres, key)?.color ?? 'var(--text-muted)';
}

export function sphereName(spheres: readonly Sphere[], key: string | null | undefined): string {
  return sphereOf(spheres, key)?.name ?? key ?? 'Personal';
}
